// =======================================================
// Entwurf der Meldung („Feedback" in der Kopfzeile)
//
// Der Entwurf lebt hier und nicht im Dialog: Wer ein Ticket anfängt und noch
// etwas nachsehen will, minimiert den Dialog, klickt weiter und kann auf
// jeder Seite ein weiteres Foto aufnehmen. Beim Seitenwechsel geht nichts
// verloren. Nur der Text wird zusätzlich im Tab gesichert (sessionStorage),
// damit ein Neuladen ihn nicht kostet — Dateien gehen dort nicht.
//
// Beim Abmelden oder Benutzerwechsel wird alles gelöscht (geteilte Geräte).
// =======================================================
import { create } from 'zustand';
import { useAuthStore } from './authStore';
import {
  MAX_ANHAENGE, dateinameFuer, screenshotAufnehmen, seitenKontextLesen,
  type MeldungErgebnis, type SeitenKontext,
} from '../lib/meldung';

export type ScreenshotStand = 'laeuft' | 'fertig' | 'fehlt';

/** Ein zusätzliches Foto, aufgenommen auf einer anderen Seite. */
export interface WeitereSeite {
  datei: File;
  kontext: SeitenKontext;
}

const SPEICHER_KEY = 'mail-feedback-entwurf';

function textSichern(text: string) {
  try {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !text.trim()) sessionStorage.removeItem(SPEICHER_KEY);
    else sessionStorage.setItem(SPEICHER_KEY, JSON.stringify({ userId, text }));
  } catch { /* privates Fenster o. Ä. — der Entwurf im Speicher reicht */ }
}

function textLesen(userId: string): string {
  try {
    const roh = sessionStorage.getItem(SPEICHER_KEY);
    if (!roh) return '';
    const s = JSON.parse(roh) as { userId?: string; text?: string };
    return s.userId === userId && typeof s.text === 'string' ? s.text : '';
  } catch {
    return '';
  }
}

function textLoeschen() {
  try { sessionStorage.removeItem(SPEICHER_KEY); } catch { /* egal */ }
}

interface Daten {
  /** Der Dialog ist offen (Gegenstück: minimiert oder gar kein Entwurf). */
  offen: boolean;
  /** Seite, auf der der Knopf geklickt wurde. Gesetzt = es gibt einen laufenden Entwurf. */
  kontext: SeitenKontext | null;
  text: string;
  screenshot: File | null;
  screenshotStand: ScreenshotStand;
  screenshotMit: boolean;
  dateien: File[];
  weitere: WeitereSeite[];
  fotoLaeuft: boolean;
  /** Kurze Rückmeldung in der Leiste („Screenshot hinzugefügt (2)"). */
  hinweis: string;
  ergebnis: MeldungErgebnis | null;
  /** Zählt mit, damit ein Foto, das nach dem Verwerfen fertig wird, ignoriert wird. */
  lauf: number;
}

interface Aktionen {
  oeffnen: () => void;
  /** `erzwingen`: auch ohne Inhalt behalten (z. B. „Andere Seite fotografieren"). */
  minimieren: (erzwingen?: boolean) => void;
  verwerfen: () => void;
  gesendet: (ergebnis: MeldungErgebnis) => void;
  schliessen: () => void;
  setText: (text: string) => void;
  setScreenshotMit: (mit: boolean) => void;
  dateienSetzen: (dateien: File[]) => void;
  weitereEntfernen: (index: number) => void;
  fotoAufnehmen: () => Promise<void>;
}

const leer = (): Omit<Daten, 'lauf'> => ({
  offen: false,
  kontext: null,
  text: '',
  screenshot: null,
  screenshotStand: 'laeuft',
  screenshotMit: true,
  dateien: [],
  weitere: [],
  fotoLaeuft: false,
  hinweis: '',
  ergebnis: null,
});

let hinweisTimer: ReturnType<typeof setTimeout> | undefined;

/** Das Foto der Ausgangsseite zählt nur mit, solange es nicht abgewählt ist. */
export function anzahlAnhaenge(s: Pick<Daten, 'screenshot' | 'screenshotMit' | 'dateien' | 'weitere'>): number {
  return (s.screenshotMit && s.screenshot ? 1 : 0) + s.dateien.length + s.weitere.length;
}

export function hatInhalt(s: Pick<Daten, 'text' | 'dateien' | 'weitere'>): boolean {
  return s.text.trim().length > 0 || s.dateien.length > 0 || s.weitere.length > 0;
}

export const useMeldungStore = create<Daten & Aktionen>()((set, get) => {
  const zuruecksetzen = () => {
    clearTimeout(hinweisTimer);
    textLoeschen();
    set((s) => ({ ...leer(), lauf: s.lauf + 1 }));
  };

  const hinweisZeigen = (text: string) => {
    clearTimeout(hinweisTimer);
    set({ hinweis: text });
    hinweisTimer = setTimeout(() => set({ hinweis: '' }), 4500);
  };

  const beginnen = () => {
    const lauf = get().lauf + 1;
    set({ kontext: seitenKontextLesen(), screenshot: null, screenshotStand: 'laeuft', lauf });
    // Foto sofort — der Dialog selbst ist ausgenommen.
    void screenshotAufnehmen().then((f) => {
      if (get().lauf !== lauf) return;
      set({ screenshot: f, screenshotStand: f ? 'fertig' : 'fehlt' });
    });
  };

  return {
    ...leer(),
    lauf: 0,

    oeffnen: () => {
      if (!get().kontext) beginnen();
      set({ offen: true });
    },

    minimieren: (erzwingen = false) => {
      const s = get();
      // Nichts getippt, nichts angehängt: es geht nichts verloren, also auch
      // keine Leiste, die danach im Weg steht.
      if (!erzwingen && !hatInhalt(s)) { zuruecksetzen(); return; }
      set({ offen: false });
    },

    verwerfen: zuruecksetzen,

    gesendet: (ergebnis) => {
      zuruecksetzen();
      set({ offen: true, ergebnis });
    },

    schliessen: zuruecksetzen,

    setText: (text) => {
      set({ text });
      textSichern(text);
    },

    setScreenshotMit: (mit) => {
      const s = get();
      if (mit && anzahlAnhaenge({ ...s, screenshotMit: true }) > MAX_ANHAENGE) {
        hinweisZeigen(`Es sind schon ${MAX_ANHAENGE} Anhänge dran — nimm erst einen anderen heraus.`);
        return;
      }
      set({ screenshotMit: mit });
    },

    dateienSetzen: (dateien) => set({ dateien }),

    weitereEntfernen: (index) => set((s) => ({ weitere: s.weitere.filter((_, i) => i !== index) })),

    fotoAufnehmen: async () => {
      const s = get();
      if (!s.kontext || s.fotoLaeuft) return;
      if (anzahlAnhaenge(s) >= MAX_ANHAENGE) {
        hinweisZeigen(`Mehr als ${MAX_ANHAENGE} Anhänge gehen nicht — nimm im Entwurf erst einen heraus.`);
        return;
      }
      const lauf = s.lauf;
      set({ fotoLaeuft: true });
      // Gelesen wird, wo die Person jetzt steht — nicht, wo sie angefangen hat.
      const kontext = seitenKontextLesen();
      const datei = await screenshotAufnehmen(dateinameFuer(kontext));
      if (get().lauf !== lauf) return; // inzwischen verworfen oder gesendet
      set({ fotoLaeuft: false });
      if (!datei) {
        hinweisZeigen('Das Foto hat nicht geklappt — häng gern selbst einen Screenshot an.');
        return;
      }
      set((alt) => ({ weitere: [...alt.weitere, { datei, kontext }] }));
      hinweisZeigen(`Screenshot hinzugefügt (${anzahlAnhaenge(get())})`);
    },
  };
});

// Abmelden oder anderes Konto: Entwurf und gesicherten Text löschen. Beim
// Neuladen (erst kein Nutzer, dann derselbe wie vorher) kommt der Text zurück.
let letzteId: string | null | undefined;
const nutzerPruefen = (s: { user: { id: string } | null }) => {
  const id = s.user?.id ?? null;
  if (id === letzteId) return;
  const vorher = letzteId;
  letzteId = id;
  if (vorher) {
    // War jemand angemeldet und jetzt nicht mehr (oder jemand anderes).
    useMeldungStore.getState().verwerfen();
  }
  if (id) {
    const text = textLesen(id);
    if (text) useMeldungStore.setState({ text });
    else textLoeschen();
  }
};
useAuthStore.subscribe(nutzerPruefen);
nutzerPruefen(useAuthStore.getState());
